/**
 * Leaderboard Table
 * Displays ranked users in a table format
 */

import { PrivateValue } from '@components/ui/PrivateValue';
import { UserProfileModal } from '@components/ui/UserProfileModal';
import { formatROI, formatStreak } from '@parlay-picker/shared/formatting';
import type { LeaderboardEntry } from '@parlay-picker/shared/hooks';
import { formatProfit } from '@parlay-picker/shared/leaderboardDisplay';
import { getStreakColor } from '@utils/colorUtils';
import { BadgeCheck } from 'lucide-react';
import { type ReactElement, useState } from 'react';

// =============================================================================
// Types
// =============================================================================

interface LeaderboardTableProps {
  entries: LeaderboardEntry[];
  showUnits?: boolean; // Show units column for actual bets
}

function getBadges(entry: LeaderboardEntry): Array<{ icon: string; label: string; color: string }> {
  const badges = [];

  // Hot Streak badge (5+ win streak)
  if (entry.stats.currentStreak >= 5) {
    badges.push({
      icon: '🔥',
      label: 'Hot Streak',
      color: 'bg-orange-500/10 text-orange-400 border-orange-500/30',
    });
  }

  // 100 Bet Club (100+ total bets)
  if (entry.stats.totalBets >= 100) {
    badges.push({
      icon: '💯',
      label: '100 Bet Club',
      color: 'bg-purple-500/10 text-purple-400 border-purple-500/30',
    });
  }

  return badges;
}

/** A row's units on Actual bets, under its profit or its Private marker: `+4.80u`, `-1.25u`. */
function UnitsLine({ units }: { units: number }): ReactElement {
  return (
    <span className={`text-xs ${units >= 0 ? 'text-theme-success/70' : 'text-theme-danger/70'}`}>
      {units >= 0 ? '+' : ''}
      {units.toFixed(2)}u
    </span>
  );
}

function getRankBadgeColor(rank: number): string {
  if (rank === 1) return 'bg-theme-warning/20 text-theme-warning border-theme-warning/30';
  if (rank === 2)
    return 'bg-theme-text-tertiary/20 text-theme-text-secondary border-theme-text-tertiary/30';
  if (rank === 3) return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
  return 'bg-theme-surface/50 text-theme-text-tertiary border-theme-border/50';
}

// =============================================================================
// Component
// =============================================================================

export function LeaderboardTable({
  entries,
  showUnits = false,
}: LeaderboardTableProps): ReactElement {
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  return (
    <>
      {selectedUserId && (
        <UserProfileModal userId={selectedUserId} onClose={() => setSelectedUserId(null)} />
      )}
      <div
        data-testid="leaderboard-table"
        className="bg-theme-surface/50 rounded-lg overflow-hidden border border-theme-border"
      >
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-theme-surface border-b border-theme-border">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-theme-text-tertiary uppercase tracking-wider">
                  Rank
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-theme-text-tertiary uppercase tracking-wider">
                  User
                </th>
                <th className="hidden @2xl:table-cell px-4 py-3 text-left text-xs font-medium text-theme-text-tertiary uppercase tracking-wider">
                  Specialty
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-theme-text-tertiary uppercase tracking-wider">
                  <div className="flex items-center justify-end gap-1">
                    <span>ROI</span>
                    <span
                      className="text-theme-text-tertiary cursor-help"
                      title="Ranking Score adjusts for sample size to prevent users with few bets from dominating. Raw shows actual performance."
                    >
                      ⓘ
                    </span>
                  </div>
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-theme-text-tertiary uppercase tracking-wider">
                  <div className="flex items-center justify-end gap-1">
                    <span>Win Rate</span>
                    <span
                      className="text-theme-text-tertiary cursor-help"
                      title="Ranking Score adjusts for sample size to prevent users with few bets from dominating. Raw shows actual performance."
                    >
                      ⓘ
                    </span>
                  </div>
                </th>
                <th className="hidden @2xl:table-cell px-4 py-3 text-right text-xs font-medium text-theme-text-tertiary uppercase tracking-wider">
                  Bets
                </th>
                <th className="hidden @2xl:table-cell px-4 py-3 text-right text-xs font-medium text-theme-text-tertiary uppercase tracking-wider">
                  Record
                </th>
                <th className="hidden @2xl:table-cell px-4 py-3 text-right text-xs font-medium text-theme-text-tertiary uppercase tracking-wider">
                  {showUnits ? 'Profit / Units' : 'Profit'}
                </th>
                <th className="hidden @2xl:table-cell px-4 py-3 text-right text-xs font-medium text-theme-text-tertiary uppercase tracking-wider">
                  Streak
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-theme-border">
              {entries.map((entry) => (
                <tr
                  key={entry.user.id}
                  onClick={() => setSelectedUserId(entry.user.id)}
                  className="hover:bg-theme-surface/50 cursor-pointer transition-colors"
                >
                  {/* Rank */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div
                      className={`inline-flex items-center justify-center w-10 h-10 rounded-lg border font-semibold ${getRankBadgeColor(
                        entry.rank,
                      )}`}
                    >
                      {entry.rank}
                    </div>
                  </td>

                  {/* User */}
                  <td className="px-4 py-4">
                    <div className="flex items-center space-x-2">
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-theme-text truncate">
                          {entry.user.displayName || 'Anonymous'}
                        </div>
                        <div className="flex items-center gap-1 mt-1 flex-wrap">
                          {entry.user.isVerified && (
                            <div className="flex items-center space-x-1 px-1.5 py-0.5 bg-theme-primary/10 rounded border border-theme-primary/30">
                              <BadgeCheck className="w-3 h-3 text-theme-primary" />
                              <span className="text-[10px] text-theme-primary font-medium">
                                Verified
                              </span>
                            </div>
                          )}
                          {getBadges(entry).map((badge, i) => (
                            <div
                              key={i}
                              className={`flex items-center space-x-1 px-1.5 py-0.5 rounded border ${badge.color}`}
                              title={badge.label}
                            >
                              <span className="text-xs">{badge.icon}</span>
                              <span className="text-[10px] font-medium">{badge.label}</span>
                            </div>
                          ))}
                        </div>
                        <div className="text-xs text-theme-text-tertiary mt-1">
                          {entry.user.followerCount}{' '}
                          {entry.user.followerCount === 1 ? 'follower' : 'followers'}
                        </div>
                      </div>
                    </div>
                  </td>

                  {/* Specialty */}
                  <td className="hidden @2xl:table-cell px-4 py-4 whitespace-nowrap">
                    {entry.specialty ? (
                      <div className="flex flex-col">
                        <span className="text-sm text-theme-text-secondary font-medium">
                          {entry.specialty.propType}
                        </span>
                        <span className="text-xs text-theme-text-tertiary">
                          {entry.specialty.winRate.toFixed(0)}% · {entry.specialty.totalBets} bets
                        </span>
                      </div>
                    ) : (
                      <span className="text-sm text-theme-text-tertiary">—</span>
                    )}
                  </td>

                  {/* ROI */}
                  <td className="px-4 py-4 whitespace-nowrap text-right">
                    <div className="flex flex-col items-end gap-0.5">
                      {entry.stats.bayesianRoi !== undefined ? (
                        <>
                          <span
                            className={`text-sm font-semibold ${
                              entry.stats.bayesianRoi >= 0
                                ? 'text-theme-success'
                                : 'text-theme-danger'
                            }`}
                            title="Ranking Score (Bayesian-adjusted for fair comparison)"
                          >
                            {formatROI(entry.stats.bayesianRoi)}
                          </span>
                          <span
                            className="text-xs text-theme-text-tertiary"
                            title="Raw ROI (actual performance)"
                          >
                            {formatROI(entry.stats.roi)} raw
                          </span>
                        </>
                      ) : (
                        <span
                          className={`text-sm font-semibold ${
                            entry.stats.roi >= 0 ? 'text-theme-success' : 'text-theme-danger'
                          }`}
                        >
                          {formatROI(entry.stats.roi)}
                        </span>
                      )}
                    </div>
                  </td>

                  {/* Win Rate */}
                  <td className="px-4 py-4 whitespace-nowrap text-right">
                    <div className="flex flex-col items-end gap-0.5">
                      {entry.stats.bayesianWinRate !== undefined ? (
                        <>
                          <span
                            className="text-sm text-theme-text-secondary"
                            title="Ranking Score (Bayesian-adjusted for fair comparison)"
                          >
                            {entry.stats.bayesianWinRate.toFixed(1)}%
                          </span>
                          <span
                            className="text-xs text-theme-text-tertiary"
                            title="Raw Win Rate (actual performance)"
                          >
                            {entry.stats.winRate.toFixed(1)}% raw
                          </span>
                        </>
                      ) : (
                        <span className="text-sm text-theme-text-secondary">
                          {entry.stats.winRate.toFixed(1)}%
                        </span>
                      )}
                    </div>
                  </td>

                  {/* Total Bets */}
                  <td className="hidden @2xl:table-cell px-4 py-4 whitespace-nowrap text-right">
                    <span className="text-sm text-theme-text-secondary">
                      {entry.stats.totalBets}
                    </span>
                  </td>

                  {/* Record */}
                  <td className="hidden @2xl:table-cell px-4 py-4 whitespace-nowrap text-right">
                    <span className="text-xs text-theme-text-tertiary">
                      {entry.stats.wins}W - {entry.stats.losses}L
                      {entry.stats.pushes > 0 && ` - ${entry.stats.pushes}P`}
                    </span>
                  </td>

                  {/* Profit / Units */}
                  <td className="hidden @2xl:table-cell px-4 py-4 whitespace-nowrap text-right">
                    {entry.stats.totalProfit === null || entry.stats.totalWagered === null ? (
                      // The owner does not share profit or stakes with this viewer; units stay public
                      <div className="flex flex-col items-end gap-0.5">
                        <PrivateValue />
                        {showUnits && entry.stats.totalUnits !== null && (
                          <UnitsLine units={entry.stats.totalUnits} />
                        )}
                      </div>
                    ) : showUnits && entry.stats.totalUnits !== null ? (
                      // Actual bets: show both profit and units
                      <div className="flex flex-col items-end gap-0.5">
                        <span
                          className={`text-sm font-semibold ${
                            entry.stats.totalProfit >= 0
                              ? 'text-theme-success'
                              : 'text-theme-danger'
                          }`}
                        >
                          {formatProfit(entry.stats.totalProfit)}
                        </span>
                        <UnitsLine units={entry.stats.totalUnits} />
                      </div>
                    ) : !showUnits && entry.stats.totalWagered > 0 ? (
                      // All tracked with some real bets: show profit
                      <span
                        className={`text-sm font-medium ${
                          entry.stats.totalProfit >= 0 ? 'text-theme-success' : 'text-theme-danger'
                        }`}
                      >
                        {formatProfit(entry.stats.totalProfit)}
                      </span>
                    ) : (
                      // All tracked with no real bets: show dash
                      <span className="text-sm text-theme-text-tertiary">—</span>
                    )}
                  </td>

                  {/* Streak */}
                  <td className="hidden @2xl:table-cell px-4 py-4 whitespace-nowrap text-right">
                    <span
                      className={`text-sm font-medium ${getStreakColor(entry.stats.currentStreak)}`}
                    >
                      {formatStreak(entry.stats.currentStreak)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
